import { FormattedDate } from 'components/misc/FormattedDate';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { Key, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

export interface LedgerRow {
    key: Key;
    date?: string | null;
    what: ReactNode;
    note?: ReactNode;
    /** The row's own action, such as Delete; the column is there only when `hasActions`. */
    action?: ReactNode;
    amount: number;
}

/**
 * An invoice's amount and what has been set against it, as the new design's General cards draw it: a plain table of
 * date, what, note and signed amount, its currency in the header, closed by what remains.
 */
export function LedgerTable({
    whatHeader,
    currency,
    rows,
    remains,
    hasActions = false,
}: {
    whatHeader: ReactNode;
    currency?: string;
    rows: LedgerRow[];
    remains: number;
    hasActions?: boolean;
}) {
    return (
        <table className="text-meta w-full border-collapse">
            <thead>
                <tr className="border-divider text-foreground-tertiary border-b">
                    <th scope="col" className="py-2 pe-2 text-start font-medium">
                        <FormattedMessage id="date" />
                    </th>
                    <th scope="col" className="px-2 py-2 text-start font-medium">
                        {whatHeader}
                    </th>
                    <th scope="col" className="px-2 py-2 text-start font-medium">
                        <FormattedMessage id="note" />
                    </th>
                    {hasActions ? (
                        <th scope="col" className="px-2 py-2">
                            <span className="sr-only">
                                <FormattedMessage id="actions" />
                            </span>
                        </th>
                    ) : null}
                    <th scope="col" className="py-2 ps-2 text-end font-medium">
                        <FormattedMessage id="invoices.details.table.amount-currency" values={{ currency }} />
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.key} className="border-divider border-b align-top">
                        <td className="py-2.5 pe-2 tabular-nums">
                            {row.date ? <FormattedDate value={row.date} /> : null}
                        </td>
                        <td className="px-2 py-2.5">{row.what}</td>
                        <td className="text-foreground-secondary px-2 py-2.5">{row.note}</td>
                        {hasActions ? <td className="px-2 py-1.5 text-end">{row.action}</td> : null}
                        <td className="py-2.5 ps-2 text-end tabular-nums">
                            <MoneyDisplay amount={row.amount} displaySign />
                        </td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr className="font-semibold">
                    <th scope="row" colSpan={hasActions ? 4 : 3} className="py-2.5 pe-2 text-start">
                        <FormattedMessage id="invoices.general-tab.revenue-team.remains" />
                    </th>
                    <td className="py-2.5 ps-2 text-end tabular-nums">
                        <MoneyDisplay amount={remains} displaySign />
                    </td>
                </tr>
            </tfoot>
        </table>
    );
}
